import { ComponentType, ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';

/**
 * StatCard — single-metric dashboard tile (T-111).
 *
 * Why this exists:
 * - DashboardPage, RestaurantSetupPage, PayrollPage, ShiftsPage etc. each
 *   re-implemented "icon + label + big number + delta" tiles with subtle drift
 *   (icon size, semantic-token misuse, currency-vs-number formatting).
 * - This primitive standardises the contract: every tile renders the same.
 *
 * Variants:
 * - `value` may be a number (auto-formatted via `format` prop), a string, or
 *   a ReactNode for fully-custom content.
 * - `format='currency'` runs the value through `formatCurrency()` (UB-008).
 * - `format='number'` uses `Intl.NumberFormat` thousand-separators.
 * - `delta` shows a colored ▲/▼ change indicator beneath the value.
 *
 * Accessibility: tile is `role='group'` with `aria-label={label}` for SR users.
 */
export interface StatCardProps {
  label: string;
  value: number | string | ReactNode;
  /** Formatter applied only when `value` is a number. */
  format?: 'currency' | 'number' | 'percent' | 'raw';
  /** Icon shown top-right; uses muted color by default. */
  icon?: LucideIcon | ComponentType<{ className?: string }>;
  /** Subtle text under the value (e.g. "Today"). A ReactNode may carry its own
   *  color (e.g. a `text-destructive-text` span) to signal state (T-202 Slice 5). */
  helper?: ReactNode;
  /** Signed delta number (e.g. +12.5 or -3) shown with ▲/▼ and color. */
  delta?: number;
  /** Override delta formatter (default: percentage to 1 decimal). */
  deltaFormat?: 'percent' | 'currency' | 'number';
  /** Tile accent color — purely cosmetic. */
  accent?: 'default' | 'success' | 'warning' | 'danger' | 'info';
  className?: string;
  onClick?: () => void;
  /** Skeleton pulse while data is loading. */
  isLoading?: boolean;
}

const ACCENT_RING: Record<NonNullable<StatCardProps['accent']>, string> = {
  default: 'border-surface/10',
  success: 'border-success/30',
  warning: 'border-warning/30',
  danger: 'border-destructive/30',
  info: 'border-info/30',
};

/** Tinted chip behind the icon — gives each tile a premium, scannable accent. */
const ACCENT_CHIP: Record<NonNullable<StatCardProps['accent']>, string> = {
  default: 'bg-primary/10 text-primary-text',
  success: 'bg-success/10 text-success-text',
  warning: 'bg-warning/10 text-warning-text',
  danger: 'bg-destructive/10 text-destructive-text',
  info: 'bg-info/10 text-info-text',
};

export function StatCard({
  label,
  value,
  format = 'raw',
  icon: Icon,
  helper,
  delta,
  deltaFormat = 'percent',
  accent = 'default',
  className,
  onClick,
  isLoading = false,
}: StatCardProps) {
  if (isLoading) {
    return (
      <div
        role="group"
        aria-label={label}
        className={cn('glass-panel p-4', ACCENT_RING[accent], className)}
      >
        <div className="h-3 w-24 animate-pulse rounded bg-muted" />
        <div className="mt-3 h-8 w-32 animate-pulse rounded bg-muted" />
        <div className="mt-2 h-3 w-20 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  let rendered: ReactNode;
  if (typeof value === 'number') {
    if (format === 'currency') rendered = formatCurrency(value);
    else if (format === 'number') rendered = new Intl.NumberFormat().format(value);
    else if (format === 'percent') rendered = `${value.toFixed(1)}%`;
    else rendered = String(value);
  } else {
    rendered = value;
  }

  const deltaText =
    typeof delta === 'number'
      ? deltaFormat === 'currency'
        ? formatCurrency(Math.abs(delta))
        : deltaFormat === 'number'
        ? new Intl.NumberFormat().format(Math.abs(delta))
        : `${Math.abs(delta).toFixed(1)}%`
      : null;

  const deltaPositive = typeof delta === 'number' ? delta >= 0 : null;

  // Clickable tiles must be keyboard-operable (T-202 Slice 5 UI/UX): expose as a button
  // with Enter/Space activation; otherwise stay a passive group.
  const interactive = Boolean(onClick);

  return (
    <div
      role={interactive ? 'button' : 'group'}
      aria-label={label}
      onClick={onClick}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={cn(
        'glass-panel min-w-0 p-4 transition-shadow',
        ACCENT_RING[accent],
        interactive && 'cursor-pointer hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        {Icon && (
          <span className={cn('flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl', ACCENT_CHIP[accent])}>
            <Icon className="h-[18px] w-[18px]" />
          </span>
        )}
      </div>
      <p className="mt-3 text-[1.7rem] font-bold leading-none tracking-tight text-foreground tabular-nums break-words">{rendered}</p>
      {(helper || deltaText) && (
        <div className="mt-1 flex items-center gap-2 text-xs">
          {deltaText && (
            <span
              className={cn(
                'inline-flex items-center gap-0.5 font-medium tabular-nums',
                deltaPositive ? 'text-success-text' : 'text-destructive-text',
              )}
            >
              <span aria-hidden>{deltaPositive ? '▲' : '▼'}</span>
              {deltaText}
            </span>
          )}
          {helper && <span className="text-muted-foreground">{helper}</span>}
        </div>
      )}
    </div>
  );
}
